// Boot-time cache hydrator.
//
// Why this exists: each useResource() in siteData.ts reads its slice of
// localStorage synchronously on mount, then refetches in useEffect. On a
// cold start (incognito, fresh device, after a cache wipe) localStorage is
// empty, so the first paint shows hardcoded fallback values until each
// per-component fetch resolves — a flash of wrong content, especially in
// admin views with many widgets.
//
// hydrateCache() prefetches the most common scopes in parallel and writes
// them into localStorage under the same `ck_cache_<key>` keys useResource
// uses. It is fire-and-forget: main.tsx kicks it off before
// ReactDOM.createRoot(...).render(...) without awaiting, so it races the
// React tree. Even partial hydration helps (each useResource will pick up
// whichever keys arrived in time on its synchronous read).
//
// We deliberately don't block render: awaiting one round-trip would delay
// initial paint by ~100-300ms, worse for perceived performance than the
// current fallback flash.

import { apiGet } from './api';
import { emitCmsChange } from './cmsEvents';

const CACHE_PREFIX = 'ck_cache_';

// Collection scopes accepted by useCollection() in siteData.ts.
// Keep this list in sync with the union there.
const COLLECTION_SCOPES = [
  'articles',
  'services',
  'projects',
  'testimonials',
  'experience',
  'certifications',
  'speaking-events',
  'thought-leadership',
  'social-links',
] as const;

function safeWrite(key: string, value: unknown): void {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(value));
  } catch {
    // localStorage may throw on quota exceeded or in private/locked-down modes.
    // Hydration is best-effort; silently skip.
  }
}

async function hydrateConfig(): Promise<void> {
  const map = await apiGet<Record<string, unknown>>('/api/config');
  if (!map || typeof map !== 'object') return;
  for (const [key, value] of Object.entries(map)) {
    safeWrite(`config_${key}`, value);
  }
}

// hydrateContent() previously prefetched /api/content into a local cache key
// consumed by useContentSection. Retired in admin-consolidation v1; section
// heading/body/visible now ship via /api/config (already hydrated by
// hydrateConfig).

async function hydrateCollection(scope: string): Promise<void> {
  const rows = await apiGet<unknown>(`/api/${scope}`);
  safeWrite(`coll_${scope}`, rows);
}

async function hydrateConversations(): Promise<void> {
  // /api/conversations is admin-gated — fetching it on a public route returns
  // 401 and surfaces in the browser console even though Promise.allSettled
  // catches the rejection. Only hydrate when the user is on an admin route
  // (the only place the conversations cache is actually consumed), and skip
  // the login page itself — there's no session yet, so a 401 is guaranteed
  // and only confuses anyone tailing the console.
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (!path.startsWith('/admin') || path.startsWith('/admin/login')) return;
  const page = await apiGet<{ rows: unknown[] }>('/api/conversations', { limit: 50 });
  if (page && Array.isArray(page.rows)) safeWrite('coll_conversations', page.rows);
}

let hydratePromise: Promise<void> | null = null;

/**
 * Prefetch the public read-only API surface and write each slice into the
 * `ck_cache_*` localStorage keys consumed by useResource() in siteData.ts.
 *
 * Idempotent: subsequent calls within a session return the same promise.
 * Fire-and-forget from main.tsx — do NOT await this in render-critical paths.
 */
export function hydrateCache(): Promise<void> {
  if (hydratePromise) return hydratePromise;
  // localStorage is unavailable in some SSR-style or hardened contexts;
  // bail out silently rather than throwing during module init.
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    hydratePromise = Promise.resolve();
    return hydratePromise;
  }

  const tasks: Array<Promise<unknown>> = [
    hydrateConfig(),
    ...COLLECTION_SCOPES.map((scope) => hydrateCollection(scope)),
    hydrateConversations(),
  ];

  hydratePromise = Promise.allSettled(tasks).then((results) => {
    // Announce that the collection caches are warm.
    //
    // useResource() refetches on its own in useEffect, so it never needed
    // this. But lib/articles.ts reads the cache through getCmsArticles() and
    // only re-reads when this bus fires — so on a cold load (incognito, fresh
    // device) the public article list stayed on the four bundled seed entries
    // until some later event, and a CMS-only article was invisible until the
    // NEXT page load warmed localStorage first.
    //
    // That is load-bearing for topic hubs: the server decides 200-vs-404 from
    // the DB, so without this the client would render "not found" on a hub the
    // server had just answered 200 for. Scope is 'articles', not 'all', so
    // useConfig does not invalidate and refetch /api/config a second time.
    emitCmsChange('articles');
    if (import.meta.env.DEV) {
      const failures = results
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => r.status === 'rejected');
      if (failures.length > 0) {
        // Quiet warning — hydration failures are non-fatal; useResource will
        // retry on mount.
        console.debug(
          `[hydrate] ${failures.length}/${results.length} prefetches failed (non-fatal)`,
        );
      }
    }
  });
  return hydratePromise;
}
